const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);

    display: block;
    font-family: var(--ctl-font);
  }

  .item{
    display: flex;
    align-items: center;
    gap: var(--ctl-space-3);
    padding: var(--ctl-list-item-padding, var(--ctl-space-3-5) var(--ctl-space-4));
    color: var(--ctl-color-text);
    background: var(--ctl-color-surface);
    transition: background-color var(--ctl-transition-normal);
    min-height: 44px;
    cursor: default;
  }

  /* interactive states */
  :host([interactive]) .item{
    cursor: pointer;
  }

  :host([interactive]) .item:hover{
    background: var(--ctl-color-hover);
  }

  :host([interactive]) .item:active{
    background: var(--ctl-color-active);
  }

  :host([selected]) .item{
    background: color-mix(in srgb, var(--ctl-color-brand) 8%, var(--ctl-color-surface));
  }

  :host([disabled]) .item{
    opacity: var(--ctl-opacity-muted);
    cursor: not-allowed;
    pointer-events: none;
  }

  /* slots */
  .prefix{
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--ctl-color-muted);
  }

  .prefix:empty{
    display: none;
  }

  .content{
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-1);
    flex: 1;
    min-width: 0;
  }

  .suffix{
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
    flex-shrink: 0;
    color: var(--ctl-color-muted);
  }

  .suffix:empty{
    display: none;
  }

  .title{
    font-size: var(--ctl-font-size-base);
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-text);
    line-height: var(--ctl-leading-normal);
  }

  .description{
    font-size: var(--ctl-font-size-md);
    color: var(--ctl-color-muted);
    line-height: var(--ctl-leading-normal);
  }

  /* hide empty slots */
  .title:empty,
  .description:empty{
    display: none;
  }
</style>

<div class="item" part="item" role="listitem">
  <div class="prefix" part="prefix">
    <slot name="prefix"></slot>
  </div>
  <div class="content" part="content">
    <div class="title" part="title">
      <slot name="title"><slot></slot></slot>
    </div>
    <div class="description" part="description">
      <slot name="description"></slot>
    </div>
  </div>
  <div class="suffix" part="suffix">
    <slot name="suffix"></slot>
  </div>
</div>
`;

export class CtlListItem extends HTMLElement {
  static get observedAttributes() {
    return ["interactive", "selected", "disabled", "value"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._item = this.shadowRoot.querySelector(".item");
    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this._onClick);
    this.addEventListener("keydown", this._onKeyDown);
    this._updateA11y();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback() {
    this._updateA11y();
  }

  // attrs
  get interactive() { return this.hasAttribute("interactive"); }
  set interactive(v) { v ? this.setAttribute("interactive", "") : this.removeAttribute("interactive"); }

  get selected() { return this.hasAttribute("selected"); }
  set selected(v) { v ? this.setAttribute("selected", "") : this.removeAttribute("selected"); }

  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { v ? this.setAttribute("disabled", "") : this.removeAttribute("disabled"); }

  get value() { return this.getAttribute("value"); }
  set value(v) { v == null ? this.removeAttribute("value") : this.setAttribute("value", String(v)); }

  _updateA11y() {
    if (this.interactive) {
      this._item.setAttribute("role", "button");
      this._item.setAttribute("tabindex", this.disabled ? "-1" : "0");
      
      if (this.selected) {
        this._item.setAttribute("aria-selected", "true");
      } else {
        this._item.removeAttribute("aria-selected");
      }

      if (this.disabled) {
        this._item.setAttribute("aria-disabled", "true");
      } else {
        this._item.removeAttribute("aria-disabled");
      }
    } else {
      this._item.setAttribute("role", "listitem");
      this._item.removeAttribute("tabindex");
      this._item.removeAttribute("aria-selected");
      this._item.removeAttribute("aria-disabled");
    }
  }

  _onClick(e) {
    if (this.disabled || !this.interactive) return;

    this.dispatchEvent(new CustomEvent("ctl-select", {
      bubbles: true,
      composed: true,
      detail: {
        value: this.value,
        selected: !this.selected
      }
    }));
  }

  _onKeyDown(e) {
    if (this.disabled || !this.interactive) return;

    // Handle Enter and Space keys for accessibility
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.dispatchEvent(new CustomEvent("ctl-select", {
        bubbles: true,
        composed: true,
        detail: {
          value: this.value,
          selected: !this.selected
        }
      }));
    }
  }
}

customElements.define("ctl-list-item", CtlListItem);
